import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import type { Language } from '@/types/language';
import { SUPPORTED_LANGUAGES } from '@/types/language';

// Default namespace and fallback configuration
export const DEFAULT_NS = 'common';
export const FALLBACK_LNG: Language = 'en';

// Available namespaces - add new products here
// common: Shared UI elements, mindcotine: MindCotine product, mindco-relief: MindCo Relief product
export const NAMESPACES = ['common', 'mindcotine', 'mindco-relief'] as const;
export type Namespace = typeof NAMESPACES[number];

// Initialize i18next with HTTP backend for JSON files
i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    // Language configuration
    lng: localStorage.getItem('preferred_language') || FALLBACK_LNG,
    fallbackLng: FALLBACK_LNG,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    
    // Namespace configuration - load all namespaces eagerly
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NS,
    fallbackNS: DEFAULT_NS,
    
    // Backend configuration - load from JSON files with build-time cache busting
    // Uses build hash instead of Date.now() to enable browser caching between deployments
    backend: {
      loadPath: `/locales/{{lng}}/{{ns}}.json?v=${import.meta.env.VITE_BUILD_HASH || 'dev'}`,
    },
    
    // React-specific options - useSuspense: false for faster initial render
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
    },
    
    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
      formatSeparator: ',',
    },
    
    // Key handling - CRITICAL: namespace:key pattern
    keySeparator: '.', // Use dot notation for nested keys
    nsSeparator: ':', // Use colon for namespace:key (e.g., 'common:nav.dashboard')
    
    // Missing key handling
    saveMissing: false,
    missingKeyHandler: (lngs, ns, key, _fallbackValue) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[i18n] Missing translation: ${ns}:${key} for languages: ${lngs.join(', ')}`);
      }
    },
    
    // Debug mode in development
    debug: process.env.NODE_ENV === 'development' && false,
  });

// Helper to change language and persist preference
export const changeLanguage = async (language: Language): Promise<void> => {
  localStorage.setItem('preferred_language', language);
  await i18n.changeLanguage(language);
  
  // Dispatch event for any non-i18n components that need to know
  window.dispatchEvent(new CustomEvent('languageChange', { detail: language }));
};

// Helper to get current language
export const getCurrentLanguage = (): Language => {
  return (i18n.language as Language) || FALLBACK_LNG;
};

// Helper to preload specific namespaces
export const preloadNamespaces = async (namespaces: Namespace[]): Promise<void> => {
  await i18n.loadNamespaces(namespaces);
};

// Helper to check if a namespace is loaded
export const isNamespaceLoaded = (namespace: Namespace, language?: Language): boolean => {
  const lng = language || getCurrentLanguage();
  return i18n.hasResourceBundle(lng, namespace);
};

export default i18n;
